import { ArrowRightOutlined, GoogleOutlined } from "@ant-design/icons"
import { Form } from "antd"
import type { ReactNode } from "react"
import { LockKeyhole, Loader } from "lucide-react"

import { Link } from "react-router"

import ButtonPrimary from "~/components/base-button"
import { BaseInputPassword } from "~/components/base-input"
import AzureAd from "~/components/icons/azure-ad"
import OSAlert from "~/components/os-alert"
import EmailField from "~/containers/auth/email-field"
import AuthSubmitButton from "~/containers/auth/submit-button"
import TermsNotice from "~/containers/auth/terms-notice"

import type { LoginFormInstance, LoginFormProps } from "./types"

const SSO_CONFIG: Record<string, { label: string; icon: ReactNode }> = {
  google: {
    label: "Google",
    icon: <GoogleOutlined className="text-xl" />
  },
  "google-oauth2": {
    label: "Google",
    icon: <GoogleOutlined className="text-xl" />
  },
  "azure-ad": {
    label: "Microsoft",
    icon: <AzureAd size={20} className="w-5 h-5" />
  }
}

export default function LoginForm({
  form,
  connections,
  filteredConnections,
  isEmailConnection,
  noAccountFound,
  isPending,
  fetcherError,
  error,
  credentialsEmail,
  redirectTo,
  generatingSsoLink,
  onFinish,
  resetForm,
  onSsoClick
}: LoginFormProps) {
  const hasConnections = (connections?.length || 0) > 0
  const isEmailReadOnly = isPending || hasConnections
  const showSubmitButton =
    !filteredConnections || filteredConnections?.length === 0

  return (
    <>
      <div className="mb-5 lg:mb-6">
        <h1
          data-testid="login-heading"
          className="login-left-heading font-bold text-dark leading-tight mb-1.5"
        >
          Welcome back to <span className="text-primary">OrbitShift</span>
        </h1>
        <p className="login-left-subtext text-gray-500">
          Sign in with your work email to pick up where you left off.
        </p>
      </div>

      <Form
        layout="vertical"
        autoComplete="off"
        spellCheck={false}
        className="login-form"
        validateTrigger="onBlur"
        onFinish={onFinish}
        form={form}
      >
        {(fetcherError || error) && !noAccountFound && (
          <OSAlert
            data-testid="login-error-alert"
            type="error"
            message={fetcherError || error}
            className="mb-3"
          />
        )}

        <EmailField
          readOnly={isEmailReadOnly}
          onEdit={hasConnections ? resetForm : undefined}
        />

        {noAccountFound && (
          <div
            data-testid="login-no-account-warning"
            className="login-alert-warning mb-3 auth-success-enter"
          >
            <p>
              {"No account found for this email. "}
              <Link
                data-testid="login-signup-link"
                to={`/register-new-user?email=${encodeURIComponent(credentialsEmail)}${redirectTo ? `&redirectTo=${encodeURIComponent(redirectTo)}` : ""}`}
                className="text-primary font-medium hover:underline"
              >
                Sign up instead
              </Link>
            </p>
          </div>
        )}

        <ConnectionFields
          filteredConnections={filteredConnections}
          generatingSsoLink={generatingSsoLink}
          onSsoClick={onSsoClick}
        />

        {showSubmitButton && (
          <SubmitButton
            form={form}
            connections={connections}
            isPending={isPending}
          />
        )}

        <TermsNotice />

        <div className="flex justify-between gap-4 mt-4 text-13">
          {connections !== null && (
            <Link
              data-testid="login-first-time-signup-link"
              to="/register"
              className="text-primary pb-[0.5px] border-b border-dashed border-primary font-medium"
            >
              First time sign up
            </Link>
          )}
          {filteredConnections?.length === 0 && isEmailConnection && (
            <Link
              data-testid="login-forgot-password-link"
              to={`/forgot-password?email=${encodeURIComponent(credentialsEmail)}`}
              className="text-primary pb-[0.5px] border-b border-dashed border-primary font-medium"
            >
              Forgot password?
            </Link>
          )}
        </div>

        {!connections && <SignUpCta redirectTo={redirectTo} />}
      </Form>
    </>
  )
}

function ConnectionFields({
  filteredConnections,
  generatingSsoLink,
  onSsoClick
}: {
  filteredConnections: { name: string }[] | undefined
  generatingSsoLink: { type: string; loading: boolean }
  onSsoClick: (connection: string) => void
}) {
  if (filteredConnections?.length === 0) {
    return (
      <Form.Item
        label="Password"
        name="_password"
        className="mb-4"
        rules={[
          {
            required: true,
            message: "Password cannot be Empty"
          }
        ]}
      >
        <BaseInputPassword
          data-testid="login-password-input"
          placeholder="Enter your password"
          autoComplete="off"
          className="login-input login-input-size !text-sm !rounded-xl shadow-sm"
          prefix={<LockKeyhole size={15} className="text-gray-400 mr-1" />}
        />
      </Form.Item>
    )
  }

  if ((filteredConnections?.length ?? 0) > 0) {
    return (
      <div className="flex gap-3 items-center mb-3">
        {filteredConnections?.map((connection) => {
          const config = SSO_CONFIG[connection.name]
          if (!config) return null
          const isLoading =
            generatingSsoLink.type === connection.name &&
            generatingSsoLink.loading
          return (
            <ButtonPrimary
              key={connection.name}
              data-testid={`login-sso-${connection.name}-button`}
              iconPosition="start"
              className="h-12 font-medium flex-1 !rounded-xl"
              onClick={() => onSsoClick(connection.name)}
            >
              {isLoading ? (
                <Loader size={20} className="animate-spin" />
              ) : (
                config.icon
              )}
              Continue with {config.label}
            </ButtonPrimary>
          )
        })}
      </div>
    )
  }

  return null
}

function SubmitButton({
  form,
  connections,
  isPending
}: {
  form: LoginFormInstance
  connections: { name: string }[] | null
  isPending: boolean
}) {
  const emailValue = Form.useWatch("_email", form)
  const passwordValue = Form.useWatch("_password", form)
  const isDisabled =
    !emailValue?.trim() || (!!connections && !passwordValue?.trim())

  return (
    <AuthSubmitButton isPending={isPending} isDisabled={isDisabled}>
      {connections ? "Continue" : "Continue with email"}
    </AuthSubmitButton>
  )
}

function SignUpCta({ redirectTo }: { redirectTo: string }) {
  return (
    <>
      <div className="login-divider mt-5">OR</div>
      <div
        data-testid="login-trial-card"
        className="login-trial-card mt-5 rounded-2xl px-6 py-5"
      >
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <p className="text-[15px] font-semibold text-dark">
              New to OrbitShift?
            </p>
            <p className="text-[13px] text-gray-600 mt-1 leading-relaxed">
              14-day free trial. All features. No credit card required.
            </p>
          </div>
          <span className="flex-shrink-0 text-[11px] font-semibold text-primary bg-primary/10 rounded-full px-3 py-1">
            Free
          </span>
        </div>
        <Link
          data-testid="login-free-trial-link"
          to={`/register-new-user${redirectTo ? `?redirectTo=${encodeURIComponent(redirectTo)}` : ""}`}
          className="login-glass-button w-full h-11 rounded-xl text-[13px] flex items-center justify-center gap-2 text-primary"
        >
          Start your free trial
          <ArrowRightOutlined />
        </Link>
      </div>
    </>
  )
}
